import { useRef, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { OperatorView } from "@/demo/OperatorView";
import { ManagerView } from "@/demo/ManagerView";
import { BrandView } from "@/demo/BrandView";
import "./demo.css";

/*
  ROLE VIEWS DEMO (/demo#platform-demo)
  Three tabs showing the operator terminal, the manager line view and the
  brand order page, all on the sample data in src/demo/data.js.
  Labels come from src/i18n/copy.js (keys starting with "demo").
  Moved here from the old pages/legacy/OldPlatform.jsx so the active site no
  longer depends on legacy files.
*/
const ROLES = [
  { id: "operator", title: "demoRoleOperator", description: "demoRoleOperatorSub", View: OperatorView },
  { id: "manager", title: "demoRoleManager", description: "demoRoleManagerSub", View: ManagerView },
  { id: "brand", title: "demoRoleBrand", description: "demoRoleBrandSub", View: BrandView },
];

const TEXT = {
  en: { title: "Role views", intro: "The same production records, shown three ways: the operator's station terminal, the manager's line view and the brand's order page.", tabs: "Choose a role" },
  ko: { title: "역할별 화면", intro: "같은 생산 기록을 세 가지 방식으로 보여줍니다. 작업자의 공정 단말, 관리자의 라인 화면, 브랜드의 오더 화면입니다.", tabs: "역할 선택" },
};

export function PlatformDemo() {
  const { t, lang } = useLang();
  const x = TEXT[lang] || TEXT.en;
  const [role, setRole] = useState("operator");
  const tabRefs = useRef([]);

  const onKey = (e, i) => {
    const n = ROLES.length;
    const next = e.key === "ArrowRight" ? (i + 1) % n : e.key === "ArrowLeft" ? (i - 1 + n) % n : e.key === "Home" ? 0 : e.key === "End" ? n - 1 : null;
    if (next === null) return;
    e.preventDefault();
    setRole(ROLES[next].id);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="platform-demo" className="demo-scope" data-testid="platform-demo" aria-labelledby="roles-title">
      <div className="demo-wrap">
        <div className="demo-head">
          <h2 id="roles-title">{x.title}</h2>
          <div>
            <p>{x.intro}</p>
            <p className="demo-note">{t("demoNote")}</p>
          </div>
        </div>

        <div className="demo-frame">
          <div role="tablist" aria-label={x.tabs} className="demo-tabs" data-testid="demo-role-switcher">
            {ROLES.map((item, i) => {
              const selected = role === item.id;
              return (
                <button key={item.id} ref={(el) => { tabRefs.current[i] = el; }} id={`platform-tab-${item.id}`} type="button" role="tab"
                  aria-selected={selected} aria-controls={`platform-panel-${item.id}`} tabIndex={selected ? 0 : -1}
                  onClick={() => setRole(item.id)} onKeyDown={(e) => onKey(e, i)} data-testid={`demo-role-${item.id}`}>
                  <span className="demo-tab-n" aria-hidden="true">{i + 1}</span>
                  <span><strong>{t(item.title)}</strong><small>{t(item.description)}</small></span>
                </button>
              );
            })}
          </div>
          {ROLES.map(({ id, View }) => (
            <div key={id} id={`platform-panel-${id}`} role="tabpanel" aria-labelledby={`platform-tab-${id}`} hidden={role !== id} tabIndex={0} className="demo-panel">
              {role === id && <div className="demo-stage" data-testid="demo-stage"><View /></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
